import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { ModalHeader } from '@/design-system/components/modal';
import { TextBody, TextCaption } from '@/design-system/components/typography';
import { ScrollView, SxProp, View } from 'dripsy';
import _, { isString, isEmpty, omit } from 'lodash';
import React, { useState, useContext, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UniInput, UniInputAmount } from '@/design-system/components/inputs';
import { LayoutContext } from 'contexts/LayoutContext';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { UpdateCardKycStackRoutes, UpdateCardKycStackParamList } from './type';
import { CardIndividualDocumentTypeSelectFilters } from '@/features/card-account/options';
import { UseInputRegularVariant } from '@/design-system/components/inputs/useInput.hook';
import { CountrySelector } from '@/features/card-account/components/countrySelector/CountrySelector';
import { DocumentUploader, DocumentUploaderProps } from '@/design-system/components/uploadDocument/DocumentUploader';
import * as DocumentPicker from 'expo-document-picker';
import { Dropdown } from '@/design-system/components/dropdown';
import { postCardaccountsClientIdentity } from '@utx/utx-access-client';
import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { ACCESS_BASE_URL } from 'App';

export interface CreateCardAccountProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  data: any;
  initData: any;
  onData: (data: any) => void;
  func?: (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
  onChangeView?: () => void;
  onBackData: (data: any) => void;
  compareData: (arg0: any, arg1: any) => object;
}

export const UpdateCardKycWebIndividualScreen: React.FC<CreateCardAccountProps> = (props) => {
  const { onLayout, data, initData, refresh, onChangeView, onBackData, compareData  } = props;
  const { t } = useTranslation('card-account');
  const layout = useContext(LayoutContext);
  const { body, textMb, btnView, btn } = sxStyles;

  const navigation = useNavigation<NativeStackNavigationProp<UpdateCardKycStackParamList>>();
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState(undefined);

  const fileFormats = ['image/png', 'image/jpeg', 'image/gif', 'application/pdf'];

  const [cardAccount, setCardAccount] = useState<any>(data);
  const [errorMsg, setErrorMsg] = useState<any>({
    dateOfBirth: undefined,
    firstName: undefined,
    lastName: undefined,
    email: undefined,
    annualIncome: undefined,
    occupation: undefined,
    position: undefined,
    mobile: undefined,
    type: undefined,
    front: undefined,
    number: undefined,
    country: undefined,
    expiryDate: undefined,
    back: undefined
  });
  const [documentUploaderSelectedDocumentFront, setDocumentUploaderSelectedDocumentFront] = useState<any>(null);
  const [documentUploaderPropsFront, setDocumentUploaderPropsFront] = useState<DocumentUploaderProps>({
    intructions: t('card-account::credit-card-new-account.glossary.upload-front'),
    onValueChange: (document: undefined | DocumentPicker.DocumentResult) => {
      setDocumentUploaderSelectedDocumentFront(document);
    },
    isLoading: undefined,
    isUploadSuccess: undefined
  });
  const handleChangeUniUploaderPropsFront = useCallback(
    (key: keyof DocumentUploaderProps, value: DocumentUploaderProps[keyof DocumentUploaderProps]) => {
      setDocumentUploaderPropsFront((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  useEffect(() => {
    if (!documentUploaderSelectedDocumentFront) return;
    if (!fileFormats.includes(documentUploaderSelectedDocumentFront?.mimeType)) {
      let cardAccountData = cardAccount;
      cardAccountData.individual.document = { ...cardAccount.individual.document, front: undefined };
      setCardAccount({
        ...cardAccountData
      });
      setErrorMsg({
        ...errorMsg,
        front: t('card-account::credit-card-new-account.glossary.accepted-file-format')
      });
      return;
    }
    if (documentUploaderSelectedDocumentFront && documentUploaderSelectedDocumentFront?.type == 'success') {
      let cardAccountData = cardAccount;
      cardAccountData.individual.document = { ...cardAccount.individual.document, front: documentUploaderSelectedDocumentFront?.uri };
      setCardAccount({
        ...cardAccountData
      });
      setErrorMsg({
        ...errorMsg,
        front: undefined
      });
    }
    handleChangeUniUploaderPropsFront('intructions', t('card-account::credit-card-new-account.glossary.upload-again'));
  }, [documentUploaderSelectedDocumentFront]);

  const [documentUploaderSelectedDocumentBack, setDocumentUploaderSelectedDocumentBack] = useState<any>(null);
  const [documentUploaderPropsBack, setDocumentUploaderPropsBack] = useState<DocumentUploaderProps>({
    intructions: t('card-account::credit-card-new-account.glossary.upload-back'),
    onValueChange: (document: undefined | DocumentPicker.DocumentResult) => {
      setDocumentUploaderSelectedDocumentBack(document);
    },
    isLoading: undefined,
    isUploadSuccess: undefined
  });
  const handleChangeUniUploaderPropsBack = useCallback(
    (key: keyof DocumentUploaderProps, value: DocumentUploaderProps[keyof DocumentUploaderProps]) => {
      setDocumentUploaderPropsBack((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  useEffect(() => {
    if (!documentUploaderSelectedDocumentBack) return;
    if (!fileFormats.includes(documentUploaderSelectedDocumentBack?.mimeType)) {
      let cardAccountData = cardAccount;
      cardAccountData.individual.document = { ...cardAccount.individual.document, back: undefined };
      setCardAccount({
        ...cardAccountData
      });
      setErrorMsg({
        ...errorMsg,
        back: t('card-account::credit-card-new-account.glossary.accepted-file-format')
      });
      return;
    }
    if (documentUploaderPropsBack && documentUploaderSelectedDocumentBack?.type == 'success') {
      let cardAccountData = cardAccount;
      cardAccountData.individual.document = { ...cardAccount.individual.document, back: documentUploaderSelectedDocumentBack?.uri };
      setCardAccount({
        ...cardAccountData
      });
      setErrorMsg({
        ...errorMsg,
        back: undefined
      });
    }
    handleChangeUniUploaderPropsBack('intructions', t('card-account::credit-card-new-account.glossary.upload-again'));
  }, [documentUploaderSelectedDocumentBack]);

  const validateForm = async (data?: { [key: string]: string | undefined }) => {
    const keys = data ? Object.keys(data) : [];
    const keysLength = keys.length;
    let validateFlag = true;
    let errorMsgFlag = { ...errorMsg };
    let emailReg = /^[^,]+@[\w-]+(\.[a-zA-Z]{2,}){1,}$/;
    let dateReg = /^[1-9]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1])$/;

    if ((!keysLength && !cardAccount?.individual?.dateOfBirth) || (keys.includes('dateOfBirth') && !data?.dateOfBirth)) {
      errorMsgFlag.dateOfBirth = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.dateOfBirth) || (keys.includes('dateOfBirth') && data?.dateOfBirth)) {
      if (!dateReg.test(!keysLength ? cardAccount?.individual?.dateOfBirth : data?.dateOfBirth)) {
        errorMsgFlag.dateOfBirth = t('card-account::credit-card-new-account.glossary.date-format-error');
        validateFlag = false;
      } else {
        errorMsgFlag.dateOfBirth = undefined;
      }
    }

    if ((!keysLength && !cardAccount?.individual?.firstName) || (keys.includes('firstName') && !data?.firstName)) {
      errorMsgFlag.firstName = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.firstName) || (keys.includes('firstName') && data?.firstName)) {
      errorMsgFlag.firstName = undefined;
    }

    if ((!keysLength && !cardAccount?.individual?.lastName) || (keys.includes('lastName') && !data?.lastName)) {
      errorMsgFlag.lastName = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.lastName) || (keys.includes('lastName') && data?.lastName)) {
      errorMsgFlag.lastName = undefined;
    }

    if ((!keysLength && !cardAccount?.individual?.email) || (keys.includes('email') && !data?.email)) {
      errorMsgFlag.email = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.email) || (keys.includes('email') && data?.email)) {
      if (!emailReg.test(!keysLength ? cardAccount?.individual?.email : data?.email)) {
        errorMsgFlag.email = t('card-account::credit-card-new-account.glossary.email-format-error');
        validateFlag = false;
      } else {
        errorMsgFlag.email = undefined;
      }
    }

    if ((!keysLength && !cardAccount?.individual?.annualIncome) || (keys.includes('annualIncome') && !data?.annualIncome)) {
      errorMsgFlag.annualIncome = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.annualIncome) || (keys.includes('annualIncome') && data?.annualIncome)) {
      errorMsgFlag.annualIncome = undefined;
    }

    if ((!keysLength && !cardAccount?.individual?.occupation) || (keys.includes('occupation') && !data?.occupation)) {
      errorMsgFlag.occupation = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.occupation) || (keys.includes('occupation') && data?.occupation)) {
      errorMsgFlag.occupation = undefined;
    }

    if ((!keysLength && !cardAccount?.individual?.position) || (keys.includes('position') && !data?.position)) {
      errorMsgFlag.position = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.position) || (keys.includes('position') && data?.position)) {
      errorMsgFlag.position = undefined;
    }

    setErrorMsg(errorMsgFlag);
    return validateFlag;
  };

  const validDocument = (data?:any)=> {
    const keys = data ? Object.keys(data) : [];
    const keysLength = keys.length;
    let validateFlag = true;
    let errorMsgFlag = { ...errorMsg };
    let dateReg = /^[1-9]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1])$/;

    if ((!keysLength && !cardAccount?.individual?.document?.type) || (keys.includes('type') && !data?.type)) {
      errorMsgFlag.type = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.document?.type) || (keys.includes('type') && data?.type)) {
      errorMsgFlag.type = undefined;
    }

    if ((!keysLength && !cardAccount?.individual?.document?.country) || (keys.includes('country') && !data?.country)) {
      errorMsgFlag.country = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.document?.country) || (keys.includes('country') && data?.country)) {
      errorMsgFlag.country = undefined;
    }

    if (
      ((!keysLength && cardAccount?.individual?.document?.expiryDate) || (keys.includes('expiryDate') && data?.expiryDate)) &&
      !dateReg.test(!keysLength ? cardAccount?.individual?.document?.expiryDate : data?.expiryDate)
    ) {
      errorMsgFlag.expiryDate = t('card-account::credit-card-new-account.glossary.date-format-error');
      validateFlag = false;
    } else {
      errorMsgFlag.expiryDate = undefined;
    }

    if ((!keysLength && !cardAccount?.individual?.document?.number) || (keys.includes('number') && !data?.number)) {
      errorMsgFlag.number = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.document?.number) || (keys.includes('number') && data?.number)) {
      errorMsgFlag.number = undefined;
    }

    if ((!keysLength && !cardAccount?.individual?.document?.front) || (keys.includes('front') && !data?.front)) {
      errorMsgFlag.front = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if (documentUploaderSelectedDocumentFront) {
      if (documentUploaderSelectedDocumentFront?.mimeType && !fileFormats.includes(documentUploaderSelectedDocumentFront?.mimeType)) {
        errorMsgFlag.front = t('card-account::credit-card-new-account.glossary.accepted-file-format');
        validateFlag = false;
      } else {
        errorMsgFlag.front = undefined;
      }
    }
    setErrorMsg(errorMsgFlag);
    return validateFlag;
  }

  const postUpdateCardKycInfo = async () => {
    const postParams:any = compareData(cardAccount, initData);
    let document = null;

    if(postParams?.individual?.document) {
      const valid = validDocument();
      if(!valid) return;
      document = {  
        ...cardAccount?.individual?.document
      }
      if( /\*/.test(document?.number)){
        setErrorMsg({number: 'The original input has been masked, please type it again to confirm.'});
        return;
      }
      if(isEmpty(cardAccount?.individual?.document?.expiryDate)){
        document = omit(document, ['expiryDate']);
      }
    }


    setLoading(true);
    try {
      const res = await postCardaccountsClientIdentity(
        {
          cardAccountId: cardAccount.cardAccountId,
          individual: {
            ...postParams?.individual,
            ...(document && { document }),
          },
        },
        {
          baseURL: ACCESS_BASE_URL
        },
      );
      setAlertMessage(undefined);
      refresh && refresh();
      navigation.navigate(UpdateCardKycStackRoutes.Placed, {
        onLayout,
        data: cardAccount,
        refresh,
      });
    } catch (error: any) {
      setAlertMessage(error?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        maxHeight: layout.dimensions.height,
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <ModalHeader
        title={t('card-account::credit-card-new-account.glossary.kyc-info')}
        closeModal={() => navigation.getParent()?.goBack()}
      />
      <ScrollView sx={{ flex: 1, backgroundColor: 'transparent' }} nestedScrollEnabled>
        <View sx={body}>
          { cardAccount?.individual?.rejectedReason &&
            <TextBody sx={textMb} semiBold>
              {t('card-account::credit-card-new-account.update-kyc-info-page.card--kyc-rejected-reason', { reason: cardAccount?.individual?.rejectedReason })}
            </TextBody>
          }
          <View sx={{ gap: '$5' }}>
            <UniInput
              regular={UseInputRegularVariant.date}
              value={cardAccount?.individual?.dateOfBirth}
              labelText={t('card-account::credit-card-new-account.glossary.date-of-birth')}
              errorText={errorMsg.dateOfBirth}
              onValueChange={(value) => {
                setCardAccount({ ...cardAccount, individual: { ...cardAccount?.individual, dateOfBirth: value } });
                validateForm({ dateOfBirth: value });
              }}
              placeholder={'yyyy-mm-dd'}
            />
            <View sx={{ gap: '$5', display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
              <UniInput
                value={cardAccount?.individual?.firstName}
                labelText={t('card-account::credit-card-new-account.glossary.first-name')}
                errorText={errorMsg.firstName}
                onValueChange={(value) => {
                  setCardAccount({
                    ...cardAccount,
                    individual: { ...cardAccount?.individual, firstName: value }
                  });
                  validateForm({ firstName: value });
                }}
              />
              <UniInput
                value={cardAccount?.individual?.lastName}
                labelText={t('card-account::credit-card-new-account.glossary.last-name')}
                errorText={errorMsg.lastName}
                onValueChange={(value) => {
                  setCardAccount({
                    ...cardAccount,
                    individual: { ...cardAccount?.individual, lastName: value }
                  });
                  validateForm({ lastName: value });
                }}
              />
            </View>
            <UniInput
              value={cardAccount?.individual?.email}
              labelText={t('card-account::credit-card-new-account.glossary.email')}
              errorText={errorMsg.email}
              onValueChange={(value) => {
                setCardAccount({
                  ...cardAccount,
                  individual: { ...cardAccount?.individual, email: value }
                });
                validateForm({ email: value });
              }}
              wrapperSx={errorMsg.email ? { pb: 10 } : undefined}
            />
            <UniInput
              value={cardAccount?.individual?.mobile}
              labelText={t('card-account::credit-card-new-account.glossary.mobile-number')}
              onValueChange={(value) => {
                setCardAccount({
                  ...cardAccount,
                  individual: { ...cardAccount?.individual, mobile: value }
                });
              }}
            />
            <UniInput
              value={cardAccount?.individual?.address}
              labelText={t('card-account::credit-card-new-account.glossary.address')}
              onValueChange={(value) => {
                setCardAccount({
                  ...cardAccount,
                  individual: { ...cardAccount?.individual, address: value }
                });
              }}
            />
            <UniInputAmount
              labelText={t('card-account::credit-card-new-account.glossary.annual-income')}
              assetPrefix={false}
              maxFlag={false}
              sxWrap={{ alignItems: 'center' }}
              asset={data?.currency}
              errorText={errorMsg.annualIncome}
              value={cardAccount?.individual?.annualIncome || 0}
              onValueChange={(value) => {
                setCardAccount({
                  ...cardAccount,
                  individual: { ...cardAccount?.individual, annualIncome: value }
                });
                validateForm({ annualIncome: value?.toString() });
              }}
            />
            <UniInput
              value={cardAccount?.individual?.occupation}
              labelText={t('card-account::credit-card-new-account.glossary.occupation')}
              errorText={errorMsg.occupation}
              onValueChange={(value) => {
                setCardAccount({
                  ...cardAccount,
                  individual: { ...cardAccount?.individual, occupation: value }
                });
                validateForm({ occupation: value });
              }}
            />
            <UniInput
              value={cardAccount?.individual?.position}
              labelText={t('card-account::credit-card-new-account.glossary.position')}
              errorText={errorMsg.position}
              onValueChange={(value) => {
                setCardAccount({
                  ...cardAccount,
                  individual: { ...cardAccount?.individual, position: value }
                });
                validateForm({ position: value });
              }}
            />
          </View>
          <View sx={{ gap: '$5', marginTop: 15 }}>
            <View sx={{ gap: '$3', zIndex: 99 }}>
              <Dropdown
                value={cardAccount?.individual?.document?.type}
                label={t('card-account::credit-card-new-account.glossary.identificaiton-document-type')}
                options={CardIndividualDocumentTypeSelectFilters(t)}
                sx={{ width: '100%' }}
                onValueChange={(value) => {
                  let cardAccountData = cardAccount;
                  cardAccountData.individual.document = { ...cardAccount.individual.document, type: value };
                  setCardAccount({
                    ...cardAccountData
                  });
                  validateForm({ type: isString(value) ? value : '' });
                }}
              />
              {errorMsg?.type && (
                <TextCaption danger sx={{ mt: '$1' }}>
                  {errorMsg?.type}
                </TextCaption>
              )}
            </View>
            <View sx={{ gap: 4, zIndex: 98 }}>
              <TextCaption semiBold>{t('card-account::credit-card-new-account.glossary.country-region')}</TextCaption>
              <CountrySelector
                value={cardAccount?.individual?.document?.country}
                onValueChange={(value) => {
                  let cardAccountData = cardAccount;
                  cardAccountData.individual.document = { ...cardAccount.individual.document, country: value };
                  setCardAccount({
                    ...cardAccountData
                  });
                  validateForm({ country: value });
                }}
              />
              {errorMsg?.country && (
                <TextCaption danger sx={{ mt: '$1' }}>
                  {errorMsg?.country}
                </TextCaption>
              )}
            </View>
            <UniInput
              regular={UseInputRegularVariant.date}
              value={cardAccount?.individual?.document?.expiryDate}
              labelText={t('card-account::credit-card-new-account.glossary.expiry-date')}
              errorText={errorMsg.expiryDate}
              onValueChange={(value) => {
                let cardAccountData = cardAccount;
                cardAccountData.individual.document = { ...cardAccount.individual.document, expiryDate: value };
                setCardAccount({
                  ...cardAccountData
                });
                validateForm({ expiryDate: value });
              }}
              placeholder={'yyyy-mm-dd'}
            />
            <UniInput
              value={cardAccount?.individual?.document?.number}
              labelText={t('card-account::credit-card-new-account.glossary.id-passport-license-number')}
              errorText={errorMsg.number}
              onValueChange={(value) => {
                let cardAccountData = cardAccount;
                cardAccountData.individual.document = { ...cardAccount.individual.document, number: value };
                setCardAccount({
                  ...cardAccountData
                });
                validDocument({ number: value });
              }}
            />
            <View sx={{ gap: '$1' }}>
              <DocumentUploader
                title={t('card-account::credit-card-new-account.glossary.front')}
                intructions={documentUploaderPropsFront.intructions}
                onValueChange={setDocumentUploaderSelectedDocumentFront}
                isLoading={documentUploaderPropsFront.isLoading}
                isUploadSuccess={documentUploaderSelectedDocumentFront && cardAccount?.individual?.document?.front ? true : false}
              />
              {errorMsg?.front && (
                <TextCaption danger sx={{ mt: '$1' }}>
                  {errorMsg?.front}
                </TextCaption>
              )}
            </View>
            <View sx={{ gap: '$1' }}>
              <DocumentUploader
                title={t('card-account::credit-card-new-account.glossary.back')}
                intructions={documentUploaderPropsBack.intructions}
                onValueChange={setDocumentUploaderSelectedDocumentBack}
                isLoading={documentUploaderPropsBack.isLoading}
                isUploadSuccess={documentUploaderSelectedDocumentBack && cardAccount?.individual?.document?.back ? true : false}
              />
              {errorMsg?.back && (
                <TextCaption danger sx={{ mt: '$1' }}>
                  {errorMsg?.back}
                </TextCaption>
              )}
            </View>
          </View>
          {!isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage || ''} sx={{ mt: 5 }} />}
          <View sx={btnView}>
            <UniButton
              sx={btn}
              variant={ButtonVariant.outline1}
              label={t('card-account-management::set-card-limit.button--back')}
              onPress={() => {
                navigation?.goBack();
                onChangeView && onChangeView();
                onBackData(cardAccount);
              }}
            />
            <UniButton
              sx={btn}
              labelSx={{ color: '$balck' }}
              loading={loading}
              label={t('card-account::credit-card-new-account.glossary.submit')}
              onPress={postUpdateCardKycInfo}
            />
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  body: {
    minHeight: 200,
    minWidth: 500,
    px: 28,
    py: 28
  },
  textMb: {
    color: 'red',
    marginBottom: 20
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '$3',
    marginTop: 40,
    zIndex: -1
  },
  btn: {
    width: 216,
    height: 44
  },
  textTitle: {
    marginBottom: '$1'
  },
  mobileView: {
    flexDirection: 'row'
  },
  mobileCodeView: {
    width: '20%',
    mr: '$2'
  },
  inputSx: {
    width: '100%'
  },
  mobileNumberView: {
    width: '80%'
  },
  account: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    py: '$5',
    px: '$4',
    borderWidth: 1,
    borderColor: '#DCDCDC',
    borderRadius: 5
  }
};
